import { arrayRemove, arrayUnion, collection, deleteDoc, doc, getDocs, limit, orderBy, query, setDoc, updateDoc, where } from "firebase/firestore";
import { uuidv4 } from '@firebase/util'

import { useState } from "react"
import { db, storage } from "lib/firebase";
import { useToast } from "@chakra-ui/react";
import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore'
import { useNavigate } from "react-router-dom";
import { COMMUNITY } from "lib/routes";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { createEmbedding } from 'hooks/createEmbedding'
const removeMd = require('remove-markdown')


export function useAddNote() {
    const [isLoading, setLoading] = useState(false)
    const toast = useToast()
    const navigate = useNavigate()

    async function addNote(note) {
        setLoading(true);
        navigate(COMMUNITY)
        const id = uuidv4();

        const embeddedTags = await generateEmbeddingTags(note.tags);

        const storageRef = ref(storage, `notes/${id}.md`);
        await uploadBytes(storageRef, note.text);
        const downloadURL = await getDownloadURL(storageRef)

        await setDoc(doc(db, "notes", id), {
            uid: note.uid,
            title: note.title,
            loadedFiles: note.loadedFiles,
            text: downloadURL,
            id,
            date: Date.now(),
            likes: [],
            tags: embeddedTags
        })
        setLoading(false)
        toast({
            title: "Note share successfully ",
            status: "success",
            isClosable: true,
            position: "top",
            duration: 5000,
        })
    }

    async function generateEmbeddingTags(tags) {
        let embeddedTags = []
        for (let i = 0; i < tags.length; i++) {
            let emmbedTag = await createEmbedding(tags[i])
            if (emmbedTag && emmbedTag.data && emmbedTag.data[0].embedding) {
                embeddedTags.push({ [tags[i]]: emmbedTag.data[0].embedding });
            }
        }
        return embeddedTags
    }

    return { addNote, isLoading }
}

export async function uploadImg(file) {
    const id = uuidv4()
    const storageRef = ref(storage, "post-sources/" + id)
    await uploadBytes(storageRef, file)

    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL
}

export async function uploadFile(file) {
    if (!file) {
        return
    }
    const id = uuidv4()
    const name = file.name
    const ext = file.type.split('/').pop();

    const storageRef = ref(storage, "note-files/" + id)
    await uploadBytes(storageRef, file)

    const downloadURL = await getDownloadURL(storageRef);
    return { downloadURL, id, name, ext }
}

export function useDeleteFile(id) {
    const [isLoading, setLoading] = useState(false)

    async function deleteComment() {
        setLoading(true)
        const desertRef = ref(storage, "note-files/" + id)
        await deleteObject(desertRef)
        setLoading(false)
    }
    return { deleteComment, isLoading }
}


export function useToggleLike({
    id,
    isLiked,
    uid
}) {
    const [isLoading, setLoading] = useState(false)
    async function toggleLike() {
        setLoading(true)

        const docRef = doc(db, "notes", id);
        await updateDoc(docRef, {
            likes: isLiked ? arrayRemove(uid) : arrayUnion(uid)
        })
        setLoading(false)
    }
    return { toggleLike, isLoading }
}

export function useDeleteNote(id) {
    const [isLoading, setLoading] = useState(false)
    const toast = useToast()
    const navigate = useNavigate()
    async function deleteNote() {
        const res = window.confirm("Are you sure you want to delete this note?")
        if (res) {
            setLoading(true)
            navigate(COMMUNITY)
            await deleteDoc(doc(db, "notes", id))


            const q = query(collection(db, "comments"), where("noteID", "==", id))
            const querySnapshot = await getDocs(q)
            querySnapshot.forEach(async (doc) => deleteDoc(doc.ref))
            toast({
                title: "Post deleted! ",
                status: "info",
                isClosable: true,
                position: "top",
                duration: 5000,
            })
            setLoading(false)
        }
    }
    return { deleteNote, isLoading };
}

export function useNote(id) {
    const q = doc(db, "notes", id)
    const [note, isLoading] = useDocumentData(q);
    return { note, isLoading }
}

export function useNotes(uid = null) {
    const q = uid ? query(collection(db, "notes"), orderBy('date', 'desc'), where('uid', '==', uid)) : query(collection(db, "notes"), orderBy('date', 'desc'))
    const [notes, isLoading, error] = useCollectionData(q);
    if (error) throw error;
    return { notes, isLoading }
}

export function useInfinityNotes(uid = null) {
    const q = uid ? query(collection(db, "notes"), orderBy('date', 'desc'), where('uid', '==', uid)) : query(collection(db, "notes"), orderBy('date', 'desc'))
    const [notes, isLoading, error] = useCollectionData(q);
    if (error) throw error;
    return { notes, isLoading }
}


export function useLastNoteFromUser(uid = null) {
    const q = uid
        ? query(collection(db, "notes"), orderBy('date', 'desc'), where('uid', '==', uid), limit(1))
        : query(collection(db, "notes"), orderBy('date', 'desc'), limit(1));

    const [notes, isLoading, error] = useCollectionData(q);
    if (error) throw error;
    return { lastNote: notes, isLoading }
}

export function useMarkdownNoteFile() {
    const [url, setURL] = useState(undefined);
    const [isLoading, setLoading] = useState(false)
    const [markdownText, setMarkdownText] = useState(null)
    async function getMarkdownFile() {
        if (url === undefined) {
            return
        }
        try {
            setLoading(true)
            const response = await fetch(`${url}`);
            const text = await response.text();
            setMarkdownText(text);
            setLoading(false)
        } catch (error) {
        }
    }
    return { setURL, getMarkdownFile, isLoading, markdownText }
}

export function useGetTextBlocksFromMarkdownsFile() {
    let textBlocks = []

    async function getTextBlocks(posts) {
        for (const { link, text: url } of posts) {
            const response = await fetch(`${url}`)
            const markdownText = await response.text()
            const text = removeMd(markdownText)
            const primerSegmento = text.substring(0, 1400);
            textBlocks.push({ link, text: primerSegmento })
        }
        return textBlocks
    }

    return { getTextBlocks }
}